/**
 * Dialog360 Diagnostics Tab
 * Connection + channel health diagnostics for the 360dialog integration.
 *
 * Note: 360dialog's Cloud API (waba-v2) has no phone-number list / quality-rating
 * endpoint, so this probes GET /health_status (can_send_message) via TestConnection.
 */

import { useEffect } from 'react';
import { IconPlugConnected } from '@tabler/icons-react';
import { Stack, Group, Button, Text, Badge, Card } from '../../../../../components/ui';
import { useDialog360 } from '../hooks/useDialog360';

function canSendColor(value?: string | null): string {
  switch ((value || '').toUpperCase()) {
    case 'AVAILABLE':
      return 'green';
    case 'LIMITED':
      return 'yellow';
    case 'BLOCKED':
      return 'red';
    default:
      return 'gray';
  }
}

export function Dialog360DiagnosticsTab() {
  const { loading, connectionResult, testConnection } = useDialog360();

  useEffect(() => {
    void testConnection();
  }, [testConnection]);

  return (
    <Stack gap="md">
      <Card>
        <Stack gap="md">
          <Group justify="space-between" align="flex-start">
            <div>
              <Text fw={500} size="lg">Connection Diagnostics</Text>
              <Text size="sm" c="dimmed">
                Probe the 360dialog channel via its health endpoint to confirm credentials
                and messaging availability. No message is sent.
              </Text>
            </div>
            <Button
              leftSection={<IconPlugConnected size={16} />}
              onClick={() => testConnection()}
              loading={loading}
              gradientVariant="primary"
            >
              Test Connection
            </Button>
          </Group>

          {connectionResult && (
            <Card withBorder padding="md" radius="md">
              <Stack gap="xs">
                <Group gap="sm">
                  <Text fw={500} size="sm">Status</Text>
                  <Badge
                    variant="light"
                    color={connectionResult.success ? 'green' : 'red'}
                    size="sm"
                  >
                    {connectionResult.success ? 'Connected' : 'Failed'}
                  </Badge>
                </Group>

                {connectionResult.canSendMessage && (
                  <Text size="sm">
                    <Text span c="dimmed">Can Send Messages: </Text>
                    <Badge variant="light" color={canSendColor(connectionResult.canSendMessage)} size="sm">
                      {connectionResult.canSendMessage}
                    </Badge>
                  </Text>
                )}

                {connectionResult.statusCode != null && (
                  <Text size="sm">
                    <Text span c="dimmed">Status Code: </Text>
                    {connectionResult.statusCode}
                  </Text>
                )}

                {connectionResult.error && (
                  <Text size="sm" c="red">
                    <Text span c="dimmed">Error: </Text>
                    {connectionResult.error}
                  </Text>
                )}
              </Stack>
            </Card>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
